import { useParams, Link } from 'react-router-dom'
import { useQuoteDetail, QUOTE_STATUS_CONFIG } from '../../api/sales'
import {
  ArrowLeft,
  FileText,
  Package,
  Wrench,
  Clock,
  DollarSign,
  CheckCircle,
  XCircle,
  AlertCircle,
  User,
  Building2,
  Calendar,
} from 'lucide-react'

export default function QuoteDetailPage() {
  const { id } = useParams<{ id: string }>()
  const quoteId = Number(id)
  const { data: quote, isLoading } = useQuoteDetail(quoteId)

  const getStatusLabel = (status: string): string => {
    const statusMap: Record<string, string> = {
      negotiating: '洽谈中',
      awaiting_confirm: '待确认',
      confirmed: '已确认',
      rejected: '已拒绝',
      cancelled: '已作废',
      draft: '草稿',
    }
    return statusMap[status] || status
  }

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'confirmed':
        return <CheckCircle className="w-5 h-5 text-green-500" />
      case 'rejected':
        return <XCircle className="w-5 h-5 text-gray-500" />
      case 'negotiating':
        return <Clock className="w-5 h-5 text-amber-500" />
      case 'awaiting_confirm':
        return <AlertCircle className="w-5 h-5 text-blue-500" />
      default:
        return <FileText className="w-5 h-5 text-gray-400" />
    }
  }

  if (isLoading) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center h-64">
          <div className="text-gray-500">加载中...</div>
        </div>
      </div>
    )
  }

  if (!quote) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center h-64">
          <div className="text-gray-500">报价单不存在</div>
        </div>
      </div>
    )
  }

  const partsItems = quote.items.filter((item) => item.itemType === 'PARTS')
  const serviceItems = quote.items.filter((item) => item.itemType === 'SERVICE')

  return (
    <div className="p-6">
      {/* 页面头部 */}
      <div className="mb-6">
        <Link
          to="/sales/quotes"
          className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4"
        >
          <ArrowLeft className="w-4 h-4" />
          返回报价单列表
        </Link>
        <div className="flex items-start justify-between">
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-semibold text-gray-900">
                {quote.quoteNo || '报价单详情'}
              </h1>
              <span
                className="inline-flex items-center gap-1.5 px-3 py-1 rounded-md text-sm font-medium"
                style={{
                  color: QUOTE_STATUS_CONFIG[quote.status as keyof typeof QUOTE_STATUS_CONFIG]?.color,
                  backgroundColor: QUOTE_STATUS_CONFIG[quote.status as keyof typeof QUOTE_STATUS_CONFIG]?.bgColor,
                }}
              >
                {getStatusIcon(quote.status)}
                {getStatusLabel(quote.status)}
              </span>
            </div>
            <p className="text-sm text-gray-500 mt-1">版本号: V{quote.versionNo}</p>
          </div>
          <div className="flex gap-2">
            {quote.status === 'negotiating' && (
              <>
                <Link
                  to={`/sales/negotiations/${quote.id}`}
                  className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition"
                >
                  查看洽谈
                </Link>
              </>
            )}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-6">
        {/* 左侧主要内容 */}
        <div className="col-span-2 space-y-6">
          {/* 报价明细 */}
          <div className="bg-white rounded-xl border border-gray-200">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="font-semibold text-gray-900">报价明细</h2>
            </div>
            <div className="p-6">
              {/* 配件费用 */}
              {partsItems.length > 0 && (
                <div className="mb-6">
                  <div className="flex items-center gap-2 mb-3">
                    <Package className="w-5 h-5 text-gray-600" />
                    <h3 className="font-medium text-gray-900">配件费用</h3>
                  </div>
                  <div className="bg-gray-50 rounded-lg overflow-hidden">
                    <table className="w-full">
                      <thead className="bg-gray-100">
                        <tr>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">
                            配件名称
                          </th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">
                            规格
                          </th>
                          <th className="px-4 py-2 text-right text-xs font-medium text-gray-500">
                            数量
                          </th>
                          <th className="px-4 py-2 text-right text-xs font-medium text-gray-500">
                            单价
                          </th>
                          <th className="px-4 py-2 text-right text-xs font-medium text-gray-500">
                            金额
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {partsItems.map((item) => (
                          <tr key={item.id} className="border-t border-gray-200">
                            <td className="px-4 py-3 text-sm">{item.itemName}</td>
                            <td className="px-4 py-3 text-sm text-gray-500">
                              {item.specification || '-'}
                            </td>
                            <td className="px-4 py-3 text-sm text-right">{item.quantity}</td>
                            <td className="px-4 py-3 text-sm text-right">
                              ¥{item.unitPrice.toLocaleString()}
                            </td>
                            <td className="px-4 py-3 text-sm text-right font-medium">
                              ¥{item.amount.toLocaleString()}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* 服务费用 */}
              {serviceItems.length > 0 && (
                <div className="mb-6">
                  <div className="flex items-center gap-2 mb-3">
                    <Wrench className="w-5 h-5 text-gray-600" />
                    <h3 className="font-medium text-gray-900">服务费用</h3>
                  </div>
                  <div className="bg-gray-50 rounded-lg overflow-hidden">
                    <table className="w-full">
                      <thead className="bg-gray-100">
                        <tr>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">
                            服务项目
                          </th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">
                            描述
                          </th>
                          <th className="px-4 py-2 text-right text-xs font-medium text-gray-500">
                            数量
                          </th>
                          <th className="px-4 py-2 text-right text-xs font-medium text-gray-500">
                            单价
                          </th>
                          <th className="px-4 py-2 text-right text-xs font-medium text-gray-500">
                            金额
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {serviceItems.map((item) => (
                          <tr key={item.id} className="border-t border-gray-200">
                            <td className="px-4 py-3 text-sm">{item.itemName}</td>
                            <td className="px-4 py-3 text-sm text-gray-500">
                              {item.description || '-'}
                            </td>
                            <td className="px-4 py-3 text-sm text-right">{item.quantity}</td>
                            <td className="px-4 py-3 text-sm text-right">
                              ¥{item.unitPrice.toLocaleString()}
                            </td>
                            <td className="px-4 py-3 text-sm text-right font-medium">
                              ¥{item.amount.toLocaleString()}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* 费用汇总 */}
              <div className="border-t border-gray-200 pt-4">
                <div className="space-y-2">
                  {quote.originalPrice && quote.originalPrice !== quote.totalAmount && (
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500">原价</span>
                      <span className="text-gray-500 line-through">
                        ¥{quote.originalPrice.toLocaleString()}
                      </span>
                    </div>
                  )}
                  {quote.discountAmount && quote.discountAmount > 0 && (
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500">优惠金额</span>
                      <span className="text-red-500">
                        -¥{quote.discountAmount.toLocaleString()}
                      </span>
                    </div>
                  )}
                  <div className="flex justify-between text-lg font-semibold pt-2 border-t border-gray-200">
                    <span className="text-gray-900">总计</span>
                    <span className="text-primary-600">
                      ¥{quote.totalAmount.toLocaleString()}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* 备注信息 */}
          {quote.remark && (
            <div className="bg-white rounded-xl border border-gray-200">
              <div className="px-6 py-4 border-b border-gray-200">
                <h2 className="font-semibold text-gray-900">备注说明</h2>
              </div>
              <div className="p-6">
                <p className="text-sm text-gray-600">{quote.remark}</p>
              </div>
            </div>
          )}

          {/* 客户反馈 */}
          {quote.customerFeedback && (
            <div className="bg-white rounded-xl border border-gray-200">
              <div className="px-6 py-4 border-b border-gray-200">
                <h2 className="font-semibold text-gray-900">客户反馈</h2>
              </div>
              <div className="p-6">
                <p className="text-sm text-gray-600">{quote.customerFeedback}</p>
              </div>
            </div>
          )}
        </div>

        {/* 右侧信息栏 */}
        <div className="space-y-6">
          {/* 基本信息 */}
          <div className="bg-white rounded-xl border border-gray-200">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="font-semibold text-gray-900">基本信息</h2>
            </div>
            <div className="p-6 space-y-4">
              <div className="flex items-start gap-3">
                <Building2 className="w-5 h-5 text-gray-400 mt-0.5" />
                <div className="flex-1">
                  <div className="text-xs text-gray-500">工单号</div>
                  <div className="text-sm font-medium text-gray-900">
                    #{quote.workOrderId}
                  </div>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <User className="w-5 h-5 text-gray-400 mt-0.5" />
                <div className="flex-1">
                  <div className="text-xs text-gray-500">销售人员</div>
                  <div className="text-sm font-medium text-gray-900">
                    {quote.salesName || '-'}
                  </div>
                </div>
              </div>
              {quote.estimatedDuration && (
                <div className="flex items-start gap-3">
                  <Clock className="w-5 h-5 text-gray-400 mt-0.5" />
                  <div className="flex-1">
                    <div className="text-xs text-gray-500">预计工期</div>
                    <div className="text-sm font-medium text-gray-900">
                      {quote.estimatedDuration}
                    </div>
                  </div>
                </div>
              )}
              <div className="flex items-start gap-3">
                <DollarSign className="w-5 h-5 text-gray-400 mt-0.5" />
                <div className="flex-1">
                  <div className="text-xs text-gray-500">币种</div>
                  <div className="text-sm font-medium text-gray-900">{quote.currency}</div>
                </div>
              </div>
            </div>
          </div>

          {/* 时间信息 */}
          <div className="bg-white rounded-xl border border-gray-200">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="font-semibold text-gray-900">时间信息</h2>
            </div>
            <div className="p-6 space-y-4">
              <div className="flex items-start gap-3">
                <Calendar className="w-5 h-5 text-gray-400 mt-0.5" />
                <div className="flex-1">
                  <div className="text-xs text-gray-500">创建时间</div>
                  <div className="text-sm text-gray-900">
                    {new Date(quote.createdAt).toLocaleString('zh-CN')}
                  </div>
                </div>
              </div>
              {quote.sentAt && (
                <div className="flex items-start gap-3">
                  <Calendar className="w-5 h-5 text-gray-400 mt-0.5" />
                  <div className="flex-1">
                    <div className="text-xs text-gray-500">发送时间</div>
                    <div className="text-sm text-gray-900">
                      {new Date(quote.sentAt).toLocaleString('zh-CN')}
                    </div>
                  </div>
                </div>
              )}
              {quote.confirmedAt && (
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-green-500 mt-0.5" />
                  <div className="flex-1">
                    <div className="text-xs text-gray-500">确认时间</div>
                    <div className="text-sm text-gray-900">
                      {new Date(quote.confirmedAt).toLocaleString('zh-CN')}
                    </div>
                  </div>
                </div>
              )}
              <div className="flex items-start gap-3">
                <Calendar className="w-5 h-5 text-gray-400 mt-0.5" />
                <div className="flex-1">
                  <div className="text-xs text-gray-500">更新时间</div>
                  <div className="text-sm text-gray-900">
                    {new Date(quote.updatedAt).toLocaleString('zh-CN')}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* 快捷操作 */}
          <div className="bg-white rounded-xl border border-gray-200">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="font-semibold text-gray-900">快捷操作</h2>
            </div>
            <div className="p-4 space-y-2">
              <Link
                to={`/sales/negotiations/${quote.id}`}
                className="block w-full px-4 py-2 text-center text-blue-600 hover:bg-blue-50 rounded-lg transition"
              >
                查看洽谈记录
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
